import { useTheme } from "@amx/ux-twoui-theming";
import { Stack as FluentStack } from "@fluentui/react";
import React from "react";
import { ComponentValidator, validateComponent } from "../../../../shared/validation";
import { isMobileViewport, useViewport } from "../../../../styles";
import { Media } from "../../../Media/Media";
import { MediaIcon, MediaImageSrc } from "../../../Media/Media.types";
import { Stack } from "../../../Stacks/Stack/Stack";
import { ChoiceGroupBaseProps, MediaPosition } from "../../shared/props";
import { getButtonText, getSelectButtonLineItems } from "../../shared/utils";
import { LineItem, SelectButtonChoiceGroupOption } from "../SelectButtonChoiceGroup/SelectButtonChoiceGroup";
import { getMediaButtonLineItemsHorizontalAlign, getMediaButtonRootStackProps, getMediaContainerStyles, mediaSize } from "./MediaButtonChoiceGroup.styles";

export interface MediaButtonChoiceGroupProps extends ChoiceGroupBaseProps {
    readonly optionType: "mediaButton";

    readonly options: readonly MediaButtonChoiceGroupOption[];

    readonly mediaPosition: MediaPosition;
}

export interface MediaButtonChoiceGroupOption extends SelectButtonChoiceGroupOption {
    /** The media item we want to add to our button */
    readonly mediaProp: MediaImageSrc | MediaIcon;
}

export type MediaButtonProps = MediaButtonChoiceGroupOption & PickedMediaButtonChoiceGroup;

type PickedMediaButtonChoiceGroup = Pick<MediaButtonChoiceGroupProps, "height" | "mediaPosition" | "optionType">;

export const buildMediaPositionAndTopRightTextValidator = (mediaPosition: MediaPosition, topRightText?: LineItem): ComponentValidator => (
    () => (topRightText && mediaPosition !== "left" ? "MediaButtonChoiceGroup must have mediaPosition set to `left` when provided a topRightText" : undefined)
);

export const MediaButton: React.FC<MediaButtonProps> = ({ text, lines, disabled, height, mediaPosition, mediaProp, selected, topRightText, optionType }) => {
    const viewportType = useViewport();
    const theme = useTheme();
    const isMobile = isMobileViewport(viewportType);

    const computedMediaProp = {
        ...mediaProp,
        shouldFadeIn: mediaProp.imageType === "image" ? false : undefined
    };

    validateComponent("MediaButtonChoiceGroup", [buildMediaPositionAndTopRightTextValidator(mediaPosition, topRightText)]);

    return (
        //Using Stack from Fluent instead of TwoUI because we can pass styles to stack in fluent but not the one in
        //TwoUI
        <FluentStack {...getMediaButtonRootStackProps(theme, isMobile, height, mediaPosition, disabled, selected)}>
            <FluentStack styles={getMediaContainerStyles(mediaPosition)}>
                <Media size={mediaSize} padding="0px" image={computedMediaProp} disabled={disabled} />
            </FluentStack>
            <Stack width="100%">
                {getButtonText(text, disabled, topRightText, optionType)}
                <Stack horizontalAlign={getMediaButtonLineItemsHorizontalAlign(mediaPosition)}>
                    {getSelectButtonLineItems(lines, disabled)}
                </Stack>
            </Stack>
        </FluentStack>
    );
};
